import { useEffect, useState, type ReactNode } from "react";
import { Header, type HeaderProps } from "./Header";
import { Sidebar } from "./Sidebar";
import type { MenuGroup } from "./menu";
import { useMediaQuery } from "./useMediaQuery";

const SIDEBAR_ID = "cx-sidebar";
const MOBILE_QUERY = "(max-width: 991.98px)";
const readCollapsed = () => {
  try {
    return localStorage.getItem("cx.sidebar") === "collapsed";
  } catch {
    return false;
  }
};
const saveCollapsed = (c: boolean) => {
  try {
    localStorage.setItem("cx.sidebar", c ? "collapsed" : "open");
  } catch {
    /* sem storage */
  }
};

type Props = Omit<HeaderProps, "onToggleSidebar" | "sidebarExpanded" | "sidebarId"> & {
  menu: MenuGroup[];
  currentPath: string;
  children: ReactNode;
  /** Painel lateral à direita do conteúdo (Copiloto, T1.14). */
  aside?: ReactNode;
};

export function AppShell({ menu, currentPath, children, aside, ...header }: Props) {
  const mobile = useMediaQuery(MOBILE_QUERY);
  // Estado salvo vale só para o desktop; no celular o menu começa sempre fechado (FE12).
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    setMobileOpen(false);
  }, [currentPath]);
  const toggle = () => {
    if (mobile) {
      setMobileOpen(!mobileOpen);
      return;
    }
    saveCollapsed(!collapsed);
    setCollapsed(!collapsed);
  };
  const isCollapsed = !mobile && collapsed;
  return (
    <div className={`wrapper d-flex flex-column min-vh-100${isCollapsed ? " sidebar-collapse" : ""}`}>
      <a href="#conteudo" className="visually-hidden-focusable position-absolute m-2 btn btn-primary">
        Pular para o conteúdo
      </a>
      <Header
        {...header}
        onToggleSidebar={toggle}
        sidebarExpanded={mobile ? mobileOpen : !collapsed}
        sidebarId={SIDEBAR_ID}
      />
      <div className="d-flex flex-grow-1 position-relative">
        <Sidebar
          key={isCollapsed ? "c" : "e"}
          id={SIDEBAR_ID}
          menu={menu}
          currentPath={currentPath}
          renderLink={header.renderLink}
          collapsed={isCollapsed}
          mobile={mobile}
          mobileOpen={mobileOpen}
        />
        {mobile && mobileOpen && (
          <div className="sidebar-backdrop" aria-hidden="true" onClick={() => setMobileOpen(false)} />
        )}
        <main id="conteudo" className="content-wrapper flex-grow-1 p-3 p-lg-4" tabIndex={-1}>
          {children}
        </main>
        {aside}
      </div>
      <footer className="main-footer border-top px-3 py-2 small text-muted">CX ERP · CXcellerate</footer>
    </div>
  );
}
